"use client";
import { useSearchParams, useRouter, usePathname } from "next/navigation";

const defaultParams: Record<string, number | string> = {
  page: 1,
  search: "",
  owner: "",
  app: "",
  shared: "all",
  folderType: "all",
  status: "all",
  memberId: "",
};

const stripDefaultParams = (params: URLSearchParams) => {
  // If the param value matches the default value specified in defaultParams above,
  // remove it from the URL object
  Object.keys(defaultParams).forEach((key) => {
    if (params.has(key) && params.get(key) === String(defaultParams[key])) {
      params.delete(key);
    }
  });
  return params;
};

export default function useRouterParams() {
  const params = useSearchParams();
  const { push } = useRouter();
  const pathname = usePathname();
  // Convert the search params to a string
  const queryString = params?.toString();
  const fullUrl = `${pathname}${queryString ? "?" + queryString : ""}`;

  const setParams = (
    paramsToSet: Record<string, string | number>,
    omit?: string[],
    pathname?: string,
    forceSet?: boolean,
  ) => {
    // Make a copy of the readonly URLSearchParams object
    // so we can update params at the end of this function.
    // This will also preserve all current params
    const newParams = new URLSearchParams(params?.toString());

    // set all new params
    Object.keys(paramsToSet).forEach((key) => {
      if (paramsToSet[key] !== undefined) {
        newParams.set(key, paramsToSet[key].toString());
      }
    });

    // remove all omitted params
    omit?.forEach((key) => {
      newParams.delete(key);
    });

    const strippedParams = stripDefaultParams(newParams);

    // If the params have changed, make a new entry into route history
    // Force set to ignore the check and always update the URL
    if (params?.toString() !== strippedParams.toString() || forceSet) {
      // If pathname is included in the function call, update the URL with the new params
      if (pathname) {
        push(`${pathname}?${strippedParams.toString()}`, { scroll: false });
        return;
      }
      push(`?${strippedParams.toString()}`, { scroll: false });
    }
  };

  const app = params?.get("app");
  const connection = params?.get("connection");
  const folderType = params?.get("folderType");
  const memberId = params?.get("memberId");
  const order = params?.get("order");
  const owner = params?.get("owner");
  const page = (params?.get("page") && Number(params?.get("page"))) || null;
  const search = params?.get("search");
  const shared = params?.get("shared");
  const status = params?.get("status");
  const virtual = params?.get("virtual");

  const ASSET_FILTER_PARAMS = [
    "app",
    "connection",
    "owner",
    "page",
    "search",
    "status",
  ];

  const OMITTED_FOLDER_QUERY_PARAMS = [
    "app",
    "connection",
    "status",
    "shared",
    "page",
    "virtual",
  ];

  const OMITTED_ZAP_QUERY_PARAMS = ["folderType", "page", "virtual"];

  // Clears all filter params
  const clearFilterParams = () => {
    setParams({}, ASSET_FILTER_PARAMS);
  };

  const clearAllParams = () => {
    setParams({}, [
      "app",
      "connection",
      "folderType",
      "memberId",
      "order",
      "owner",
      "page",
      "search",
      "shared",
      "status",
      "virtual",
    ]);
  };

  return {
    app,
    clearFilterParams,
    clearAllParams,
    connection,
    folderType,
    fullUrl,
    memberId,
    OMITTED_FOLDER_QUERY_PARAMS,
    OMITTED_ZAP_QUERY_PARAMS,
    order,
    owner,
    page,
    search,
    setParams,
    shared,
    status,
    virtual,
  };
}
